import { v4 as uuidv4 } from "uuid";
import Config from "../../common/config";
import * as jwt from "../../common/lib/jwt";
import ApiRequest, {
  setAuthorizationHeader,
  removeAuthorizationHeader,
} from "../../common/lib/apiRequest";
import ERROR_CODES from "../../common/constants/errorCodes";
import ERROR_MESSAGES from "../../common/constants/errorMessages";

import router from "../../router";
const MAX_LOGIN_ATTEMPS = 3;
const mutationTypes = {
  LOGIN: "auth/LOGIN",
  SET_LOGIN_ERROR: "auth/SET_LOGIN_ERROR",
  SET_LOGIN_VERIFY_CAPTCHA: "auth/SET_LOGIN_VERIFY_CAPTCHA",
  SET_LOGIN_VERIFICATION: "auth/SET_LOGIN_VERIFICATION",
  SET_LOGIN_VERIFY_TWO_FA: "auth/SET_LOGIN_VERIFY_TWO_FA",
  SET_LOGIN_RESPONSE: "auth/SET_LOGIN_RESPONSE",
  SET_AUTH: "auth/SET_AUTH",
  PURGE_AUTH: "auth/PURGE_AUTH",
  FORGET_PASSWORD: "auth/FORGET_PASSWORD",
  SET_FORGET_PASSWORD_RESPONSE: "auth/SET_FORGET_PASSWORD_RESPONSE",
  RESET_PASSWORD: "auth/RESET_PASSWORD",
  SET_RESET_PASSWORD_RESPONSE: "auth/SET_RESET_PASSWORD_RESPONSE",
  SIGNUP: "auth/SIGNUP",
  VERIFY_SIGNUP_EMAIL: "auth/VERIFY_SIGNUP_EMAIL",
  SET_SIGNUP_RESPONSE: "auth/SET_SIGNUP_RESPONSE",
  VERIFY_LOGIN: "auth/VERIFY_LOGIN",
  SET_VERIFY_LOGIN_RESPONSE: "auth/SET_VERIFY_LOGIN_RESPONSE",
  VERIFY_SIGNUP_INVITATION_CODE: "auth/VERIFY_SIGNUP_INVITATION_CODE",
  DISABLE_ACCOUNT: "auth/DISABLE_ACCOUNT",
  SET_DISABLE_ACCOUNT_RESPONSE: "auth/SET_DISABLE_ACCOUNT_RESPONSE",
};

const state = {
  isAuthenticated: false,
  accessToken: null,
  user: null,
  login: {
    isLoggingIn: false,
    verificationRequired: false,
    recaptchaSiteKey: Config.recaptchaSiteKey,
    reCaptchaRequired: false,
    twoFaRequired: false,
    error: null,
  },
  signup: {
    isSubmitting: false,
    isSubmitted: false,
    codeSending: false,
    codeSent: false,
    codeSendingError: null,
    error: null,
    invitationCodeVerifying: false,
    invitationCodeVerifyingError: null,
    betaAccessInitialForm: {},
  },
  forgetPassword: {
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  },
  resetPassword: {
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  },
  loginVerification: {
    verifying: true,
    verified: false,
    error: null,
  },
  disableAccount: {
    isSubmitting: false,
    isSubmitted: false,
    error: null,
  },
};

export const actions = {
  async login(context, payload) {
    if (context.state.login.reCaptchaRequired && !payload.recaptchaResponse) {
      return context.commit(
        mutationTypes.SET_LOGIN_ERROR,
        new Error(ERROR_MESSAGES[ERROR_CODES.INVALID_RECAPTCHA_TOKEN])
      );
    }
    context.commit(mutationTypes.LOGIN);
    try {
      jwt.destroyRefreshToken();
      const res = await ApiRequest.post("/auth/login", {
        user: payload.email,
        passwd: payload.password,
        token: payload.recaptchaResponse,
        verificationCode: payload.verificationCode,
      });
      const { token: accessToken, refreshToken } = res;
      context.commit(mutationTypes.SET_AUTH, {
        accessToken,
        refreshToken,
        user: jwt.decodeToken(accessToken),
        keepLoggedIn: payload.keepLoggedIn,
      });
      context.dispatch("checkAndRefreshToken");
    } catch (error) {
      switch (error.error_code) {
        case ERROR_CODES.WRONG_USERNAME_OR_PASSWORD:
        case ERROR_CODES.INVALID_RECAPTCHA_TOKEN: {
          error.message =
            ERROR_MESSAGES[ERROR_CODES.WRONG_USERNAME_OR_PASSWORD];
          break;
        }
        case ERROR_CODES.NEW_IP_DETECTED: {
          context.commit(mutationTypes.SET_LOGIN_VERIFICATION);
          break;
        }
        case ERROR_CODES.TWO_FA_VERIFICATION_CODE_INVALID: {
          context.commit(mutationTypes.SET_LOGIN_RESPONSE, {
            twoFaRequired: false,
            isLoggingIn: false,
          });
          break;
        }
        case ERROR_CODES.TWO_FA_VERIFICATION_CODE_NOT_FOUND: {
          context.commit(mutationTypes.SET_LOGIN_VERIFY_TWO_FA);
          return;
        }
      }
      if (error.result.loginAttemptFailed >= MAX_LOGIN_ATTEMPS) {
        context.commit(mutationTypes.SET_LOGIN_VERIFY_CAPTCHA);
      }
      context.commit(mutationTypes.SET_LOGIN_ERROR, error);
    }
  },
  async oauthLogin({ commit, dispatch }, { provider, payload }) {
    const res = await ApiRequest.post("/auth/oauth_login/" + provider, payload);
    const { token: accessToken, refreshToken } = res;
    commit(mutationTypes.SET_AUTH, {
      accessToken,
      refreshToken,
      user: jwt.decodeToken(accessToken),
      keepLoggedIn: true,
    });
    dispatch("checkAndRefreshToken");
  },
  async requestToken({ commit }, payload) {
    const data = await ApiRequest.get(
      `/auth/oauth/twitter/request_token?callback_url=${payload.callbackUrl}`
    );
    return {
      token: data.oauth_token,
      tokenSecret: data.oauth_token_secret,
    };
  },
  resetLoginForm({ commit }) {
    commit(mutationTypes.SET_LOGIN_RESPONSE, {
      isLoggingIn: false,
      verificationRequired: false,
      reCaptchaRequired: false,
      error: null,
    });
  },
  async getAuth(context) {
    const accessToken =
      jwt.getToken() || context.rootState.route.query?.accessToken;
    if (!accessToken) {
      jwt.destroyRefreshToken();
      return;
    }
    const ret = await checkTokenValid(accessToken);
    if (ret.valid) {
      context.commit(mutationTypes.SET_AUTH, {
        accessToken: ret.accessToken,
        user: jwt.decodeToken(accessToken),
      });
      context.dispatch("checkAndRefreshToken");
    } else {
      const refreshToken = jwt.getRefreshToken();
      if (!refreshToken) return;
      try {
        const { token: newAccessToken } = await ApiRequest.post(
          "/auth/refresh_token",
          { token: accessToken },
          {
            headers: {
              [Config.requestHeaders.refreshToken]: refreshToken,
            },
          }
        );
        context.commit(mutationTypes.SET_AUTH, {
          accessToken: newAccessToken,
          user: jwt.decodeToken(newAccessToken),
        });
        context.dispatch("checkAndRefreshToken");
      } catch (error) {
        jwt.destroyRefreshToken();
      }
    }
  },
  logout(context) {
    context.dispatch("setChunkLoading", true, { root: true });
    jwt.destroyTokens();
    jwt.destroyRefreshToken();
    window.location.reload();
  },
  async forgetPassword(context, email) {
    context.commit(mutationTypes.FORGET_PASSWORD, email);
    try {
      await ApiRequest.post("/auth/forget_password", { email });
      context.commit(mutationTypes.SET_FORGET_PASSWORD_RESPONSE, {
        isSubmitting: false,
        isSubmitted: true,
      });
    } catch (error) {
      context.commit(mutationTypes.SET_FORGET_PASSWORD_RESPONSE, {
        isSubmitting: false,
        error: error,
      });
    }
  },
  async resetPassword(context, { password, token }) {
    context.commit(mutationTypes.RESET_PASSWORD);
    try {
      await ApiRequest.post(
        "/auth/reset_password",
        { new_passwd: password },
        {
          headers: {
            [Config.requestHeaders.resetPasswordToken]: token,
          },
        }
      );
      context.commit(mutationTypes.SET_RESET_PASSWORD_RESPONSE, {
        isSubmitting: false,
        isSubmitted: true,
      });
    } catch (error) {
      context.commit(mutationTypes.SET_RESET_PASSWORD_RESPONSE, {
        isSubmitting: false,
        error: error,
      });
    }
  },
  async signup(context, payload) {
    context.commit(mutationTypes.SIGNUP);
    try {
      await ApiRequest.post("/auth/register", {
        email: payload.email,
        passwd: payload.password,
        first_name: payload.firstName,
        last_name: payload.lastName,
        confirmation_code: String(payload.emailCode).replace(/[^0-9]/g, ""),
        invitation_code: payload.invitationCode,
        subscribe_mailing_list: payload.subscribed,
        token: payload.token,
      });
      context.commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        isSubmitting: false,
        isSubmitted: true,
      });
    } catch (error) {
      context.commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        isSubmitting: false,
        error,
      });
    }
  },
  async verifySignupEmail(context, email) {
    context.commit(mutationTypes.VERIFY_SIGNUP_EMAIL);
    try {
      await ApiRequest.post("/auth/send_confirmation_code", {
        email,
      });
      context.commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        codeSending: false,
        codeSent: true,
      });
    } catch (error) {
      context.commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        codeSending: false,
        codeSent: false,
        codeSendingError: error,
      });
    }
  },
  async checkEmailExisted({}, email) {
    try {
      const emailExisted = await ApiRequest.post("/auth/is_email_in_used", {
        email,
      });
      return {
        emailExisted,
      };
    } catch (error) {
      return {
        emailExisted: true,
      };
    }
  },
  async verifyLogin({ commit }, token) {
    commit(mutationTypes.VERIFY_LOGIN);
    try {
      const { token: accessToken } = await ApiRequest.post(
        "/auth/login_verification",
        {},
        {
          headers: {
            [Config.requestHeaders.authToken]: token,
          },
        }
      );
      commit(mutationTypes.SET_VERIFY_LOGIN_RESPONSE, {
        accessToken,
        user: jwt.decodeToken(accessToken),
      });
    } catch (error) {
      commit(mutationTypes.SET_VERIFY_LOGIN_RESPONSE, {
        error: error,
      });
    }
  },
  async verifySignupInvitationCode({ commit }, { email, invitationCode }) {
    commit(mutationTypes.VERIFY_SIGNUP_INVITATION_CODE, {
      email,
      invitationCode,
    });
    try {
      await ApiRequest.post("/auth/validate_invitation_code", {
        email,
        invitation_code: invitationCode,
      });
      commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        invitationCodeVerifying: false,
      });
    } catch (error) {
      commit(mutationTypes.SET_SIGNUP_RESPONSE, {
        invitationCodeVerifying: false,
        invitationCodeVerifyingError: error,
      });
    }
  },
  async disableAccount({ commit }, token) {
    commit(mutationTypes.DISABLE_ACCOUNT);
    try {
      await ApiRequest.post(
        "/user/deactivate",
        {},
        {
          headers: {
            [Config.requestHeaders.authToken]: token,
          },
        }
      );
      commit(mutationTypes.SET_DISABLE_ACCOUNT_RESPONSE, {
        isSubmitted: true,
      });
    } catch (error) {
      commit(mutationTypes.SET_DISABLE_ACCOUNT_RESPONSE, {
        error: error,
      });
    }
  },
  resetDisableAccount({ commit }) {
    commit(mutationTypes.SET_DISABLE_ACCOUNT_RESPONSE, {
      error: null,
    });
  },
  async checkAndRefreshToken({ commit, state, dispatch }) {
    const accessToken = state.accessToken;
    const tokenDecoded = jwt.decodeToken(accessToken);
    const tokenExipryInMs = tokenDecoded?.exp * 1000 - Date.now() || 0;
    if (tokenExipryInMs <= 30000) {
      const refreshToken = jwt.getRefreshToken();
      if (refreshToken) {
        try {
          const { token: newAccessToken } = await ApiRequest.post(
            "/auth/refresh_token",
            {
              token: accessToken,
            },
            {
              headers: {
                [Config.requestHeaders.refreshToken]: refreshToken,
              },
            }
          );
          const newAccessTokenDecoded = jwt.decodeToken(newAccessToken);
          commit(mutationTypes.SET_AUTH, {
            accessToken: newAccessToken,
            user: newAccessTokenDecoded,
          });
          setTimeout(() => {
            dispatch("checkAndRefreshToken");
          }, newAccessTokenDecoded.exp * 1000 - Date.now() - 10000);
        } catch (error) {
          console.error(error);
        }
      }
    } else {
      setTimeout(() => {
        dispatch("checkAndRefreshToken");
      }, tokenExipryInMs - 15000);
    }
  },
  async guestLogin({ commit, dispatch }) {
    const res = await ApiRequest.post("/guest/login", {
      id: uuidv4(),
    });
    const { token: accessToken, refreshToken } = res;
    commit(mutationTypes.SET_AUTH, {
      accessToken,
      refreshToken,
      user: jwt.decodeToken(accessToken),
    });
    dispatch("checkAndRefreshToken");
  },
};

export const mutations = {
  [mutationTypes.SET_AUTH](
    state,
    { accessToken, refreshToken, user, keepLoggedIn } = {}
  ) {
    state.isAuthenticated = true;
    state.user = user;
    state.login = {
      ...state.login,
      isLoggingIn: false,
      error: null,
      reCaptchaRequired: false,
    };
    state.accessToken = accessToken;
    setAuthorizationHeader(accessToken);
    jwt.saveToken(accessToken, keepLoggedIn);
    refreshToken && jwt.saveRefreshToken(refreshToken);
  },
  [mutationTypes.SET_LOGIN_ERROR](state, error) {
    state.login = {
      ...state.login,
      isLoggingIn: false,
      error: error,
    };
  },
  [mutationTypes.PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.login = {
      ...state.login,
      isLoggingIn: false,
      error: null,
    };
    jwt.destroyTokens();
    removeAuthorizationHeader();
  },
  [mutationTypes.LOGIN](state) {
    state.login = {
      ...state.login,
      isLoggingIn: true,
      error: null,
    };
  },
  [mutationTypes.SET_LOGIN_RESPONSE](state, data) {
    state.login = {
      ...state.login,
      ...data,
    };
  },
  [mutationTypes.SET_LOGIN_VERIFY_CAPTCHA](state) {
    state.login.isLoggingIn = false;
    state.login.reCaptchaRequired = true;
  },
  [mutationTypes.SET_LOGIN_VERIFICATION](state) {
    state.login.isLoggingIn = false;
    state.login.verificationRequired = true;
  },
  [mutationTypes.SET_LOGIN_VERIFY_TWO_FA](state) {
    state.login.isLoggingIn = false;
    state.login.reCaptchaRequired = false;
    state.login.twoFaRequired = true;
  },
  [mutationTypes.FORGET_PASSWORD](state) {
    state.forgetPassword = {
      isSubmitting: true,
      isSubmitted: false,
      error: null,
    };
  },
  [mutationTypes.SET_FORGET_PASSWORD_RESPONSE](state, data) {
    state.forgetPassword = data;
  },
  [mutationTypes.RESET_PASSWORD](state) {
    state.resetPassword = {
      isSubmitting: true,
      isSubmitted: false,
      error: null,
    };
  },
  [mutationTypes.SET_RESET_PASSWORD_RESPONSE](state, data) {
    state.resetPassword = data;
  },
  [mutationTypes.SIGNUP](state) {
    state.signup = {
      isSubmitting: true,
      isSubmitted: false,
      error: null,
    };
  },
  [mutationTypes.VERIFY_SIGNUP_EMAIL](state) {
    state.signup = {
      ...state.signup,
      codeSending: true,
      codeVerified: false,
      codeSendingError: null,
    };
  },
  [mutationTypes.SET_SIGNUP_RESPONSE](state, data) {
    state.signup = {
      ...state.signup,
      ...data,
    };
  },
  [mutationTypes.VERIFY_LOGIN](state) {
    state.loginVerification = {
      verifying: true,
    };
  },
  [mutationTypes.SET_VERIFY_LOGIN_RESPONSE](
    state,
    { error, accessToken, user }
  ) {
    state.loginVerification = {
      verifying: false,
      error,
    };
    if (accessToken && user) {
      state.isAuthenticated = true;
      state.user = user;
      setAuthorizationHeader(accessToken);
      jwt.saveToken(accessToken);
      redirectToHomePage();
    }
  },
  [mutationTypes.VERIFY_SIGNUP_INVITATION_CODE](
    state,
    { email, invitationCode }
  ) {
    state.signup = {
      ...state.signup,
      invitationCodeVerifying: true,
      invitationCodeVerifyingError: null,
      betaAccessInitialForm: { email, invitationCode },
    };
  },
  [mutationTypes.DISABLE_ACCOUNT](state) {
    state.disableAccount = {
      isSubmitting: true,
      isSubmitted: false,
      error: null,
    };
  },
  [mutationTypes.SET_DISABLE_ACCOUNT_RESPONSE](state, data) {
    state.disableAccount = {
      isSubmitting: false,
      ...data,
    };
  },
};

const getters = {
  user(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  login(state) {
    return state.login;
  },
  forgetPassword(state) {
    return state.forgetPassword;
  },
  resetPassword(state) {
    return state.resetPassword;
  },
  signup(state) {
    return state.signup;
  },
  loginVerification(state) {
    return state.loginVerification;
  },
  disableAccount: state => state.disableAccount,
  accessToken: state => state.accessToken,
  isGuest: state => state.user?.role === "guest",
  isUser: state => state.user?.role === "user",
};

/* istanbul ignore next */
const checkTokenValid = async token => {
  try {
    await ApiRequest.get("/user", {
      headers: {
        [Config.requestHeaders.authToken]: token,
      },
    });
    return {
      valid: true,
      accessToken: token,
    };
  } catch (error) {
    return {
      valid: false,
    };
  }
};

const redirectToHomePage = () => {
  const currentRoute = router.currentRoute;
  const next = currentRoute.query.next;
  router.push(
    next || {
      name: "Home",
    }
  );
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
